import React, { useCallback, useState } from "react";
import { View, SafeAreaView, ScrollView, Image, Pressable } from "react-native";
import { useRouter, usePathname } from "expo-router";
import Logo from "./feature/header/logo";
import {
  useOrganization,
  useUser,
  useOrganizationList,
  useAuth,
  UserButton,
} from "@clerk/clerk-react";
import DropdownMenu from "./common/dropdown/dropdown-menu";
import { MenuOption } from "./common/dropdown/menu-option";
import StyledText from "./common/ui/styled-text";
import { useDrawer } from "../contexts/DrawerContext";
import AgentStatus from "./agent-status";
import { useActiveAgent } from "../contexts/ActiveAgentContext";
import { useSettingsData } from "../contexts/SettingsDataContext";
import PauseModal from "./feature/home/pause-modal";
import { OrganizationSwitcherModal } from "./common/organization-switcher-modal";
import { usePostHog } from "posthog-js/react";
import {
  Home,
  Phone,
  PieChart,
  Settings,
  HelpCircle,
  ChevronDown,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  CreditCard,
  Banknote,
  Sparkles,
  Plug,
} from "lucide-react-native";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { Badge } from "./badge";
import { useViewport } from "../hooks/useViewport";

export default function DrawerContent() {
  const router = useRouter();
  const currentPath = usePathname();
  const { isPaymentsFeatureEnabled } = useFeatureFlags();

  const { isCollapsed, toggleCollapsed } = useDrawer();
  const {
    activeAgent,
    agentsLoading: loading,
    enableActiveAgent,
    pauseActiveAgent,
    disableActiveAgent,
  } = useActiveAgent();
  const { settings } = useSettingsData();
  const fallbackNumber = settings?.fallbackPhoneNumber;

  const posthog = usePostHog();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { userMemberships } = useOrganizationList({
    userMemberships: true,
  });
  const isMultiOrg =
    user?.organizationMemberships?.length &&
    user?.organizationMemberships?.length > 1;
  const { signOut, orgRole } = useAuth();
  const isAdmin = orgRole === "org:admin";

  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);

  type Route = {
    name: string;
    path: string;
    icon:
      | "home"
      | "phone-call"
      | "pie-chart"
      | "credit-card"
      | "settings"
      | "help-circle"
      | "banknote";
    external?: boolean;
    fake?: boolean;
    showNewBadge?: boolean;
  };
  const routes: Route[] = [
    { name: "Overview", path: "/", icon: "home" },
    {
      name: "Calls & Orders",
      path: "/calls-and-orders",
      icon: "phone-call",
    },
    {
      name: "Reports (Coming Soon!)",
      path: "/reports",
      icon: "pie-chart",
    },
    ...(isPaymentsFeatureEnabled()
      ? [
          {
            name: "Earnings",
            path: "/earnings",
            icon: "banknote" as const,
            showNewBadge: true,
          },
        ]
      : []),
    {
      name: "Settings",
      path: "/settings",
      icon: "settings",
    },
    {
      name: "Support",
      path: "/support",
      icon: "help-circle",
    },
  ];

  const handleRoutePress = (path: string) => {
    router.push(path);
  };

  const { isMobile } = useViewport();

  const [showPauseMenu, setShowPauseMenu] = useState(false);

  const handleToggle = useCallback(
    async (newSettingState: boolean): Promise<void> => {
      if (!activeAgent) return;

      posthog.capture("agent_toggled", {
        is_enabled: newSettingState,
      });

      // turning it off
      if (!newSettingState) {
        setShowPauseMenu(true);
      }
      // turning it on
      else {
        enableActiveAgent();
      }
    },
    [activeAgent, setShowPauseMenu, enableActiveAgent]
  );

  const handlePauseSelection = useCallback(
    async (hours: number) => {
      if (!activeAgent) return;

      setShowPauseMenu(false);

      posthog.capture("agent_paused", {
        hours: hours === Infinity ? -1 : hours,
      });

      const indefinitelyPaused = hours === Infinity;
      if (indefinitelyPaused) {
        disableActiveAgent();
      } else {
        pauseActiveAgent(hours);
      }
    },
    [activeAgent, setShowPauseMenu, disableActiveAgent, pauseActiveAgent]
  );

  return (
    <SafeAreaView className="flex-1 bg-white">
      <View
        className={`p-2 px-4 border-b border-gray-200 flex items-center h-16 ${
          isCollapsed ? "justify-center" : "justify-between"
        }`}
      >
        {isCollapsed ? (
          <Pressable
            onPress={toggleCollapsed}
            className="p-2 rounded-md hover:bg-gray-100 hidden sm:flex"
          >
            <ChevronRight size={20} color="#1f2937" />
          </Pressable>
        ) : (
          <View className="flex flex-row items-center gap-2 justify-between w-full">
            <Logo />
            <Pressable
              onPress={toggleCollapsed}
              className="p-2 rounded-md hover:bg-gray-100 hidden sm:flex"
            >
              <ChevronLeft size={20} color="#1f2937" />
            </Pressable>
          </View>
        )}
      </View>

      <ScrollView className="flex-1">
        {routes.map((route) => {
          const isActive =
            route.path === "/"
              ? currentPath === "/"
              : currentPath.startsWith(route.path);
          return (
            <Pressable
              key={route.name}
              className={`active:bg-gray-50 flex-row items-center py-4 ${
                isCollapsed ? "px-4 justify-center" : "px-4"
              } ${isActive ? "bg-gray-100" : ""}`}
              onPress={() => (route.fake ? {} : handleRoutePress(route.path))}
            >
              {route.icon === "home" ? (
                <Home size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : route.icon === "phone-call" ? (
                <Phone size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : route.icon === "pie-chart" ? (
                <PieChart size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : route.icon === "credit-card" ? (
                <CreditCard size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : route.icon === "settings" ? (
                <Settings size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : route.icon === "help-circle" ? (
                <HelpCircle size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : route.icon === "banknote" ? (
                <Banknote size={16} color={isActive ? "#000" : "#6B7280"} />
              ) : null}
              {!isCollapsed && (
                <View className="ml-4 flex-row items-center">
                  <StyledText
                    className={`text-sm ${
                      isActive ? "text-black font-medium" : "text-gray-500"
                    }`}
                  >
                    {route.name}
                  </StyledText>
                  {route.showNewBadge && (
                    <Badge
                      variant="secondary"
                      className="ml-3 bg-blue-100 text-blue-800 border-blue-200 !gap-1 font-normal"
                    >
                      <Sparkles size={10} color="#1e40af" />
                      New
                    </Badge>
                  )}
                </View>
              )}
            </Pressable>
          );
        })}
      </ScrollView>

      <View className="w-full border-t border-b border-border p-2">
        <AgentStatus
          status={activeAgent?.status ?? null}
          fallbackNumber={fallbackNumber ?? null}
          compact={isCollapsed}
          loading={loading}
          handleActivate={() => handleToggle(true)}
          handleDeactivate={() => handleToggle(false)}
        />
      </View>

      <View className={`${isCollapsed ? "p-2" : "p-4"}`}>
        <DropdownMenu
          containerClassName="w-full"
          alignment="center"
          dropdownPosition="above"
          dropdownWidth={220}
          visible={actionMenuVisible}
          handleOpen={() => setActionMenuVisible(true)}
          handleClose={() => setActionMenuVisible(false)}
          trigger={
            <View
              className={`flex flex-1 w-full flex-row items-center gap-3 rounded-md p-2 hover:bg-gray-100 cursor-pointer ${
                isCollapsed ? "justify-center mb-[10px]" : ""
              }`}
            >
              <Image
                source={{
                  uri: isMultiOrg
                    ? (user?.imageUrl ??
                      organization?.imageUrl ??
                      "default_avatar.png")
                    : (organization?.imageUrl ??
                      user?.imageUrl ??
                      "default_avatar.png"),
                }}
                className="w-8 h-8 rounded-full"
              />
              {!isCollapsed && (
                <>
                  <View className="flex flex-1 flex-col overflow-hidden gap-1">
                    <StyledText className="truncate font-medium">
                      {user?.primaryEmailAddress?.emailAddress}
                    </StyledText>
                    <StyledText className="truncate text-xs text-gray-500">
                      {organization?.name}
                    </StyledText>
                  </View>
                  <ChevronDown size={16} color="#1f2937" />
                </>
              )}
            </View>
          }
        >
          {userMemberships?.data && (
            <MenuOption
              onSelect={() => {
                router.push("/organization");
              }}
            >
              <View className="flex-row items-center space-x-3">
                <Image
                  source={{
                    uri: organization?.imageUrl ?? "default_org_photo.png",
                  }}
                  className="w-5 h-5 rounded-full"
                  resizeMode="cover"
                />
                <StyledText className="text-gray-800">Account</StyledText>
              </View>
            </MenuOption>
          )}

          <MenuOption
            onSelect={() => {
              router.push("/profile");
            }}
            className="gap-3 flex-row items-center flex justify-start"
          >
            <UserButton
              appearance={{
                elements: {
                  avatarBox: "w-5 h-5",
                },
              }}
            />
            <StyledText className="text-gray-800">Profile</StyledText>
          </MenuOption>
          {isMultiOrg && (
            <MenuOption
              onSelect={() => {
                setShowOrganizationModal(true);
                setActionMenuVisible(false);
                if (isMobile) {
                  // close the drawer
                  router.back();
                }
              }}
              className="gap-3 flex-row items-center flex justify-start"
            >
              <View className="flex-row items-center space-x-4">
                <ArrowLeftRight size={16} color="#1f2937" />
                <StyledText className="text-gray-800">
                  Switch Organization
                </StyledText>
                <ChevronRight size={16} color="#1f2937" />
              </View>
            </MenuOption>
          )}
          <MenuOption
            onSelect={() => {
              router.push("/integrations");
            }}
          >
            <View className="gap-3 flex-row items-center flex justify-start">
              <Plug size={20} color="#1f2937" />
              <StyledText className="text-gray-800">Integrations</StyledText>
            </View>
          </MenuOption>
          {isAdmin && (
            <>
              <MenuOption
                onSelect={() => {
                  router.push("/billing");
                }}
                className="gap-3 flex-row items-center flex justify-start"
              >
                <CreditCard size={20} color="#1f2937" />
                <StyledText className="text-gray-800">Billing</StyledText>
              </MenuOption>
            </>
          )}

          <MenuOption
            onSelect={() => {
              signOut();
              router.push("/");
            }}
          >
            <View className="flex-row items-center space-x-4">
              <LogOut size={20} color="#991b1b" />
              <StyledText className="text-red-800">Logout</StyledText>
            </View>
          </MenuOption>
        </DropdownMenu>
      </View>

      <PauseModal
        visible={showPauseMenu}
        onSelectDuration={handlePauseSelection}
        onClose={() => setShowPauseMenu(false)}
      />
      <OrganizationSwitcherModal
        visible={showOrganizationModal}
        onClose={() => setShowOrganizationModal(false)}
      />
    </SafeAreaView>
  );
}
